import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../api';
import useDashboardData from '../hooks/useDashboardData';
import useConversationContext from '../hooks/useConversationContext';
import usePortfolioHealth from '../hooks/usePortfolioHealth';
import useQuantAnalysis from '../hooks/useQuantAnalysis';
import useOptimizer from '../hooks/useOptimizer';
import { useCurrency } from '../contexts/CurrencyContext';
import FrontierChart from './charts/FrontierChart';
import LabSection from './LabSection';
import ChartModal from './ChartModal';
import { FirstRun, GuideStrip } from './FirstRun';
import Disclaimer from './Disclaimer';
import useTitle from '../hooks/useTitle';
import { chartRequestHandler } from '../api';
import { track } from '../utils/analytics';
import {
  formatNoteDate, formatEtClock, usMarketStatus, MARKET_STATUS_LABEL, timeAgo, formatEarningsDate,
} from '../utils/marketTime';
import { fmtPct, fmtNum, fmtMoney, signClass } from '../utils/format';
import './PortfolioNote.css';

// The portfolio note: one page that replaces the old Dashboard and Portfolio
// Analytics tabs. Data comes from the existing hooks; this file only decides
// what to show and in which order.

const INDEX_NAMES = { '^GSPC': 'S&P 500', '^DJI': 'Dow', '^IXIC': 'NASDAQ', '^RUT': 'Russell 2000' };

const METHODS = [
  ['markowitz', 'Markowitz'],
  ['ledoit_wolf', 'Ledoit–Wolf'],
  ['black_litterman', 'Black–Litterman'],
  ['risk_parity', 'Risk parity'],
];

const METRICS = [
  ['sharpe_ratio', 'Sharpe', 'ratio'],
  ['sortino_ratio', 'Sortino', 'ratio'],
  ['var_95', 'VaR 95 · one day', 'pct'],
  ['cvar_95', 'Expected shortfall', 'pct'],
  ['max_drawdown', 'Max drawdown', 'pct'],
  ['beta', 'Beta', 'ratio'],
];

const SECTIONS = [
  ['summary', 'Summary'],
  ['risk', 'Risk'],
  ['frontier', 'Frontier'],
  ['lab', 'Lab'],
  ['holdings', 'Holdings'],
  ['health', 'Health'],
  ['earnings', 'Earnings & news'],
];

const hhiLabel = (h) => (h == null ? '' : h > 2500 ? 'high' : h > 1500 ? 'moderate' : 'low');
const healthLabel = (s) => (s == null ? '' : s >= 80 ? 'Excellent' : s >= 60 ? 'Good' : s >= 40 ? 'Fair' : 'Needs work');

function limitLine(info, noun) {
  if (!info || info.daily_limit == null) return null;
  if (info.user_tier === 'admin' || info.daily_limit === Infinity) return null;
  return `${info.remaining ?? '—'} of ${info.daily_limit} ${noun} left today`;
}

function metricDisplay(key, kind, metric, money) {
  const v = metric?.value;
  if (v == null) return { value: '—', sub: '' };
  if (kind === 'ratio') {
    return { value: fmtNum(v, 2), sub: metric.explanation || (metric.rating ? String(metric.rating) : '') };
  }
  const pct = fmtPct(v * 100, { digits: 1, sign: v < 0 });
  let sub = metric.explanation || '';
  if (!sub && key === 'max_drawdown') {
    sub = metric.recovery_days != null ? `Recovered in ${metric.recovery_days} days.` : 'Not yet recovered.';
  } else if (!sub && metric.dollar_amount != null) {
    sub = `${money(Math.abs(metric.dollar_amount))} on a bad day.`;
  } else if (!sub && metric.rating) {
    sub = String(metric.rating);
  }
  return { value: pct, sub };
}

const Skeleton = ({ w = '60%', h = 12 }) => <span className="pn-sk" style={{ width: w, height: h }} />;

export default function PortfolioNote({ user, onLogout }) {
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const dash = useDashboardData(user);
  const { userProfile, updateUserProfile, loading: profileLoading } = useConversationContext(user);
  useTitle('Portfolio note');
  const health = usePortfolioHealth(user, userProfile);
  const quant = useQuantAnalysis(user, userProfile);
  const opt = useOptimizer(user, userProfile);

  const [method, setMethod] = useState('markowitz');
  const [views, setViews] = useState({});
  const [summaries, setSummaries] = useState({});
  const [summarizing, setSummarizing] = useState(null);
  const [chart, setChart] = useState(null);

  const requestedQuant = useRef(false);
  const requestedHealth = useRef(false);
  const requestedOpt = useRef(false);

  const money = useCallback((n) => formatCurrency(n), [formatCurrency]);
  const signedMoney = (n) => {
    const v = Number(n) || 0;
    return `${v > 0 ? '+' : v < 0 ? '−' : ''}${money(Math.abs(v))}`;
  };

  // Risk figures load once per holdings set; the hook caches them for an hour.
  useEffect(() => {
    if (!dash.hasHoldings || !quant.quantHydrated || quant.quantData || quant.quantLoading || requestedQuant.current) return;
    requestedQuant.current = true;
    quant.fetchQuantAnalysis();
  }, [dash.hasHoldings, quant]);

  useEffect(() => {
    if (!health.qualifiesForReport || health.healthData || health.loading || requestedHealth.current) return;
    requestedHealth.current = true;
    health.triggerReport();
  }, [health]);

  useEffect(() => {
    if (!opt.hasMinHoldings || !opt.optimizerHydrated || opt.optimizerData || opt.optimizerLoading || requestedOpt.current) return;
    requestedOpt.current = true;
    opt.fetchOptimizer('markowitz');
  }, [opt]);

  useEffect(() => {
    if (opt.currentMethod) setMethod(opt.currentMethod);
  }, [opt.currentMethod]);

  const handleSummarize = useCallback(async (index, article) => {
    if (summaries[index]) {
      setSummaries((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      return;
    }
    setSummarizing(index);
    try {
      const res = await api.post('/v1/dashboard/summarize-article', {
        headline: article.headline,
        summary: article.summary || '',
        source: article.source || '',
        user_id: user?.uid,
      });
      setSummaries((prev) => ({ ...prev, [index]: res.data.summary }));
    } catch (err) {
      setSummaries((prev) => ({ ...prev, [index]: 'Could not summarise this one. Try again in a moment.' }));
    } finally {
      setSummarizing(null);
    }
  }, [summaries, user?.uid]);

  const runOptimizer = () => {
    const cleanViews = {};
    Object.entries(views).forEach(([t, v]) => {
      const n = parseFloat(v);
      if (Number.isFinite(n)) cleanViews[t] = n;
    });
    opt.refreshOptimizer(method, method === 'black_litterman' ? cleanViews : null);
  };

  const status = usMarketStatus();
  const p = dash.portfolio;

  // ── States that replace the whole page ──
  if (dash.authError && dash.error) {
    return (
      <div className="pn pn-state">
        <h1 className="dd-serif">Session expired</h1>
        <p className="dd-muted">Sign in again to continue.</p>
        <button type="button" className="dd-btn" onClick={onLogout}>Sign in</button>
      </div>
    );
  }
  if (dash.error && !p) {
    return (
      <div className="pn pn-state">
        <h1 className="dd-serif">Couldn't load your note</h1>
        <p className="dd-muted">{dash.error}</p>
        <button type="button" className="dd-btn" onClick={() => dash.refresh()}>Try again</button>
      </div>
    );
  }
  if (dash.loading && !p) {
    return (
      <div className="pn" aria-busy="true" aria-label="Loading your portfolio note">
        <div className="pn-head"><Skeleton w="34%" /><Skeleton w="20%" /></div>
        <div className="pn-section"><Skeleton w="30%" h={44} /><div className="pn-gap" /><Skeleton w="70%" /><Skeleton w="62%" /></div>
        <div className="pn-tiles">{METRICS.map(([k]) => <div key={k} className="pn-tile"><Skeleton w="40%" h={10} /><Skeleton w="55%" h={30} /><Skeleton w="80%" h={10} /></div>)}</div>
        <div className="pn-section"><Skeleton w="100%" h={220} /></div>
      </div>
    );
  }
  if (!dash.hasHoldings || !p) {
    return (
      <div className="pn">
        {profileLoading ? (
          <p className="dd-muted">Loading your profile…</p>
        ) : (
          <FirstRun userProfile={userProfile} onSave={updateUserProfile} onSaved={() => dash.refresh()} />
        )}
      </div>
    );
  }

  const holdings = (p.holdings || []).slice().sort((a, b) => (b.weight || 0) - (a.weight || 0));
  const dailyPct = p.value ? (p.daily_estimated_pnl / p.value) * 100 : 0;
  const o = opt.optimizerData?.optimization;
  const optExplanation = opt.optimizerData?.ai_explanation?.overall_recommendation;
  const metrics = quant.quantData?.metrics;
  const h = health.healthData;
  const tickers = (userProfile?.holdings || []).map((x) => String(x.ticker || '').toUpperCase()).filter(Boolean);
  const openChart = (row) => {
    const held = (userProfile?.holdings || []).find((x) => String(x.ticker || '').toUpperCase() === row.ticker);
    setChart({ symbol: row.ticker, title: row.name, position: held ? { quantity: held.quantity, avgPrice: held.avgPrice } : { quantity: row.quantity, avgPrice: row.avg_price } });
    track('chart_open', { from: 'note' });
  };

  return (
    <div className="pn">
      <div className="pn-head">
        <div className="dd-mono dd-muted pn-dateline">
          Portfolio note · {formatNoteDate()} · prices {formatEtClock()} · {MARKET_STATUS_LABEL[status]}{p.base_currency ? ` · values in ${p.base_currency}` : ''}
        </div>
        <div className="pn-actions">
          <button type="button" className="dd-btn-ghost" onClick={() => navigate('/settings')}>Edit holdings</button>
          <button type="button" className="dd-btn" onClick={() => dash.refresh()}>Refresh</button>
        </div>
      </div>

      <nav className="pn-subnav dd-mono" aria-label="On this note">
        {SECTIONS.map(([id, label]) => <a key={id} href={`#${id}`}>{label}</a>)}
      </nav>

      {dash.indices && Object.keys(dash.indices).length > 0 && (
        <div className="pn-tape dd-mono">
          {Object.entries(dash.indices).map(([key, d]) => (
            <span key={key}>
              <span className="pn-tape-name">{INDEX_NAMES[key] || key}</span>{' '}
              <span className="dd-num">{Number(d?.price || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>{' '}
              <span className={`dd-num ${signClass(d?.percent_change)}`}>{fmtPct(d?.percent_change, { digits: 2 })}</span>
            </span>
          ))}
        </div>
      )}

      <GuideStrip user={user} userProfile={userProfile} holdings={userProfile?.holdings || []} />

      {/* ── Summary ── */}
      <section id="summary" className="pn-section pn-summary">
        <div>
          <div className="pn-value-row">
            <span className="dd-serif dd-num pn-value">{money(p.value)}</span>
            <span className={`dd-num pn-today ${signClass(p.daily_estimated_pnl)}`}>
              {signedMoney(p.daily_estimated_pnl)} today · {fmtPct(dailyPct, { digits: 2 })}
            </span>
          </div>
          {dash.aiBriefing && (
            <p className="pn-briefing">
              {dash.aiBriefing}
              {dash.sentiment && <span className="pn-tag">{dash.sentiment}</span>}
            </p>
          )}
        </div>
        <div className="pn-facts dd-mono">
          <div><span>Since cost</span><span className={`dd-num ${signClass(p.total_return)}`}>{fmtPct(p.total_return_pct)} · {signedMoney(p.total_return)}</span></div>
          <div><span>Concentration</span><span className="dd-num">HHI {Number(p.hhi_concentration || 0).toLocaleString()} · {hhiLabel(p.hhi_concentration)}</span></div>
          <div><span>Beta</span><span className="dd-num">{fmtNum(p.weighted_beta, 2)}</span></div>
          <div><span>Sectors</span><span className="dd-num">{p.sector_count ?? '—'}</span></div>
          <div><span>Dividend yield</span><span className="dd-num">{fmtNum(p.weighted_dividend_yield, 2)}%</span></div>
        </div>
      </section>

      {/* ── Risk ── */}
      <section id="risk" className="pn-section">
        <div className="pn-section-head">
          <div className="dd-eyebrow">Risk · 252 trading days · vs S&amp;P 500</div>
          <div className="pn-section-tools dd-mono dd-muted">
            {limitLine(quant.quantRateLimitInfo, 'runs')}
            <button type="button" className="pn-textbtn" onClick={() => quant.refreshQuantAnalysis()} disabled={quant.quantLoading}>
              {quant.quantLoading ? 'Computing…' : 'Recompute'}
            </button>
          </div>
        </div>
        {quant.quantError && !metrics && (
          <p className="pn-error">{String(quant.quantError)}</p>
        )}
        <div className="pn-tiles">
          {METRICS.map(([key, label, kind]) => {
            const m = metrics?.[key];
            if (!metrics && quant.quantLoading) {
              return <div key={key} className="pn-tile"><div className="dd-eyebrow">{label}</div><Skeleton w="55%" h={30} /><Skeleton w="80%" h={10} /></div>;
            }
            if (m?.locked) {
              return (
                <div key={key} className="pn-tile pn-tile--locked">
                  <div className="dd-eyebrow">{label}</div>
                  <div className="pn-tile-value dd-serif">Pro</div>
                  <div className="pn-tile-sub dd-muted"><a href="/premium">Unlock with Pro</a></div>
                </div>
              );
            }
            const { value, sub } = metricDisplay(key, kind, m, money);
            const cls = kind === 'pct' && m?.value < 0 ? 'dd-loss' : '';
            return (
              <div key={key} className="pn-tile">
                <div className="dd-eyebrow">{label}</div>
                <div className={`pn-tile-value dd-serif dd-num ${cls}`}>{value}</div>
                {sub && <div className="pn-tile-sub dd-muted">{sub}</div>}
              </div>
            );
          })}
        </div>
        {quant.quantData?.overall_summary && <p className="pn-summary-text">{quant.quantData.overall_summary}</p>}
      </section>

      {/* ── Frontier ── */}
      <section id="frontier" className="pn-section">
        <div className="pn-section-head">
          <div className="dd-eyebrow">Frontier · where you sit</div>
          <div className="pn-section-tools dd-mono dd-muted">{limitLine(opt.optimizerRateLimitInfo, 'optimiser runs')}</div>
        </div>
        {!opt.hasMinHoldings ? (
          <p className="dd-muted">Add a second holding to see the efficient frontier.</p>
        ) : (
          <div className="pn-frontier">
            <div>
              {o ? (
                <FrontierChart
                  frontier={o.efficient_frontier}
                  current={o.current_portfolio}
                  maxSharpe={o.max_sharpe}
                  minVol={o.min_volatility}
                  riskParity={method === 'risk_parity' ? o.risk_parity : undefined}
                  height={300}
                  caption={`${METHODS.find(([k]) => k === (o.method || method))?.[1] || 'Markowitz'}, long-only, ${opt.optimizerData?.data_coverage?.trading_days_analyzed || 252} trading days. The dashed line is the move from where you are to the best return per unit of risk.`}
                />
              ) : (
                <FrontierChart caption={opt.optimizerLoading ? 'Computing the frontier…' : (opt.optimizerError ? String(opt.optimizerError) : 'Run the optimiser to draw your frontier.')} height={300} />
              )}
            </div>
            <div className="pn-frontier-side">
              <label className="dd-eyebrow" htmlFor="pn-method">Method</label>
              <select id="pn-method" className="pn-select" value={method} onChange={(e) => setMethod(e.target.value)}>
                {METHODS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
              {method === 'black_litterman' && (
                <div className="pn-views">
                  <div className="dd-eyebrow">Your views · expected annual return %</div>
                  {tickers.map((t) => (
                    <label key={t} className="pn-view-row dd-mono">
                      <span>{t}</span>
                      <input type="number" step="0.5" placeholder="—" value={views[t] ?? ''} onChange={(e) => setViews((v) => ({ ...v, [t]: e.target.value }))} />
                    </label>
                  ))}
                </div>
              )}
              <button type="button" className="dd-btn" onClick={runOptimizer} disabled={opt.optimizerLoading}>
                {opt.optimizerLoading ? 'Optimising…' : 'Run optimiser'}
              </button>
              {opt.optimizerError && o && <p className="pn-error">{String(opt.optimizerError)}</p>}
              {o && (
                <table className="pn-table dd-num pn-weights">
                  <thead><tr><th>Ticker</th><th className="r">Now</th><th className="r">Max Sharpe</th><th className="r">Min vol</th><th className="r">Risk parity</th></tr></thead>
                  <tbody>
                    {o.tickers.map((t) => (
                      <tr key={t}>
                        <td className="dd-mono">{t}</td>
                        <td className="r">{fmtNum(o.current_portfolio?.weights?.[t], 1)}%</td>
                        <td className="r">{fmtNum(o.max_sharpe?.weights?.[t], 1)}%</td>
                        <td className="r">{fmtNum(o.min_volatility?.weights?.[t], 1)}%</td>
                        <td className="r">{fmtNum(o.risk_parity?.weights?.[t], 1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {typeof optExplanation === 'string' && optExplanation && <p className="pn-summary-text">{optExplanation}</p>}
            </div>
          </div>
        )}
      </section>

      {/* ── Lab ── */}
      <LabSection user={user} userProfile={userProfile} rows={p.holdings || []} baseCurrency={p.base_currency || 'USD'} money={money} optimizer={opt.optimizerData} />

      {/* ── Holdings ── */}
      <section id="holdings" className="pn-section">
        <div className="pn-section-head">
          <div className="dd-eyebrow">Holdings · {holdings.length}</div>
          <div className="pn-section-tools dd-mono dd-muted"><span>Click a ticker for its chart</span><button type="button" className="pn-textbtn" onClick={() => navigate('/settings')}>Edit</button></div>
        </div>
        <div className="pn-scroll">
          <table className="pn-table dd-num pn-holdings">
            <thead>
              <tr><th>Ticker</th><th>Weight</th><th className="r">Price</th><th className="r">Today</th><th className="r">Value</th><th className="r">Since cost</th><th>Sector</th></tr>
            </thead>
            <tbody>
              {holdings.map((row) => (
                <tr key={row.ticker}>
                  <td><button type="button" className="pn-ticker-btn" onClick={() => openChart(row)} title={`Chart ${row.ticker}`}><span className="dd-mono pn-ticker">{row.ticker}</span></button><div className="dd-muted pn-name">{row.name}</div></td>
                  <td>
                    <div className="pn-bar"><div className="pn-bar-fill" style={{ width: `${Math.min(100, row.weight || 0)}%` }} /><span className="dd-mono">{fmtNum(row.weight, 1)}%</span></div>
                  </td>
                  <td className="r">{fmtMoney(row.price, row.native_currency || 'USD', 2)}</td>
                  <td className={`r ${signClass(row.percent_change)}`}>{fmtPct(row.percent_change)}</td>
                  <td className="r">{money(row.current_value)}</td>
                  <td className={`r ${signClass(row.return_pct)}`}>{fmtPct(row.return_pct)}</td>
                  <td className="dd-muted">{row.sector}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Health ── */}
      <section id="health" className="pn-section">
        <div className="pn-section-head">
          <div className="dd-eyebrow">Health review</div>
          <div className="pn-section-tools dd-mono dd-muted">
            {health.rateLimitInfo && health.rateLimitInfo.daily_limit != null && `${health.rateLimitInfo.remaining_actions ?? '—'} of ${health.rateLimitInfo.daily_limit} reviews left today`}
            {health.qualifiesForReport && (
              <button type="button" className="pn-textbtn" onClick={() => health.refreshReport()} disabled={health.loading}>
                {health.loading ? 'Reviewing…' : 'Refresh review'}
              </button>
            )}
          </div>
        </div>
        {!health.qualifiesForReport ? (
          <p className="dd-muted">The review needs at least two holdings and a half-complete profile. <button type="button" className="pn-textbtn pn-textbtn--inline" onClick={() => navigate('/settings')}>Finish your profile</button></p>
        ) : !h ? (
          <p className="dd-muted">{health.loading ? 'Reviewing your portfolio…' : (health.error ? String(health.error) : 'No review yet.')}</p>
        ) : (
          <div className="pn-health">
            <div className="pn-health-score">
              <span className="dd-serif dd-num">{Math.round(h.health_score ?? 0)}</span>
              <span className="dd-eyebrow">{healthLabel(h.health_score)} · risk {h.risk_analysis?.risk_level || '—'}</span>
              {h.risk_analysis?.volatility_assessment && <p className="dd-muted">{h.risk_analysis.volatility_assessment}</p>}
            </div>
            <div>
              <div className="dd-eyebrow">Sectors</div>
              <div className="pn-sectors">
                {(h.sector_allocation?.sectors || []).map((s) => (
                  <div key={s.name} className="pn-sector">
                    <span>{s.name}</span>
                    <div className="pn-bar"><div className="pn-bar-fill" style={{ width: `${Math.min(100, s.percentage || 0)}%` }} /><span className="dd-mono">{fmtNum(s.percentage, 0)}%</span></div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="dd-eyebrow">Recommendations</div>
              <ul className="pn-list">
                {(h.recommendations || []).map((r, i) => (
                  <li key={`${r.title}-${i}`}>
                    <span className={`pn-priority pn-priority--${r.priority || 'low'}`}>{r.priority || 'note'}</span>
                    <div><strong>{r.title}</strong><div className="dd-muted">{r.description}</div></div>
                  </li>
                ))}
                {(h.insights || []).map((ins, i) => (
                  <li key={`${ins.title}-${i}`}>
                    <span className="pn-priority pn-priority--info">{ins.type || 'note'}</span>
                    <div><strong>{ins.title}</strong><div className="dd-muted">{ins.message}</div></div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </section>

      {/* ── Earnings & news ── */}
      <section id="earnings" className="pn-section pn-two">
        <div>
          <div className="dd-eyebrow">Upcoming earnings</div>
          {dash.earnings?.length ? (
            <table className="pn-table dd-num">
              <tbody>
                {dash.earnings.map((ev, i) => (
                  <tr key={`${ev.ticker}-${i}`}>
                    <td className="dd-mono pn-ticker">{ev.ticker}</td>
                    <td>{formatEarningsDate(ev.date)}{ev.hour ? <span className="dd-muted"> · {ev.hour === 'bmo' ? 'pre-market' : ev.hour === 'amc' ? 'after hours' : 'during market'}</span> : null}</td>
                    <td className="dd-muted">{ev.eps_estimate != null ? `est. EPS $${Number(ev.eps_estimate).toFixed(2)}` : ''}</td>
                    <td className="r"><span className={`pn-tag ${ev.is_estimated ? '' : 'pn-tag--ok'}`}>{ev.is_estimated ? 'Estimated' : 'Confirmed'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="dd-muted">No earnings dates in the next 90 days for your names.</p>
          )}

          <div className="pn-research">
            <div className="pn-section-head">
              <div className="dd-eyebrow">News read against your holdings</div>
              <div className="pn-section-tools dd-mono dd-muted">
                {health.newsRateLimitInfo?.weekly_limit != null && `${health.newsRateLimitInfo.remaining_analyses ?? 0} of ${health.newsRateLimitInfo.weekly_limit} this week`}
              </div>
            </div>
            {health.newsData?.report?.formatted_report ? (
              <div className="pn-markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{health.newsData.report.formatted_report}</ReactMarkdown>
                <button type="button" className="pn-textbtn" onClick={() => health.refreshNewsAnalysis()} disabled={health.newsLoading}>{health.newsLoading ? 'Reading…' : 'Read again'}</button>
              </div>
            ) : user?.isPremium ? (
              <div>
                <p className="dd-muted">A written read of this week's news for your names, with the mechanism, not just the headline.</p>
                <button type="button" className="dd-btn-ghost" onClick={() => health.refreshNewsAnalysis()} disabled={health.newsLoading}>{health.newsLoading ? 'Reading the news…' : 'Read the news for me'}</button>
                {health.newsError && <p className="pn-error">{String(health.newsError)}</p>}
              </div>
            ) : (
              <p className="dd-muted">Pro reads the week's news against your holdings. <a href="/premium">See Pro</a></p>
            )}
          </div>
        </div>

        <div>
          <div className="dd-eyebrow">News on your names</div>
          {dash.news?.length ? (
            <ul className="pn-news">
              {dash.news.map((a, i) => (
                <li key={`${a.url}-${i}`}>
                  {a.ticker_tag && <span className="pn-tag pn-tag--ticker dd-mono">{a.ticker_tag}</span>}
                  <a href={a.url} target="_blank" rel="noopener noreferrer">{a.headline}</a>
                  <div className="pn-news-meta dd-mono dd-muted">
                    <span>{a.source}</span><span>{timeAgo(a.datetime)}</span>
                    <button type="button" className="pn-textbtn" onClick={() => handleSummarize(i, a)} disabled={summarizing === i}>
                      {summarizing === i ? 'Reading…' : summaries[i] ? 'Hide' : 'What it means for me'}
                    </button>
                  </div>
                  {summaries[i] && <p className="pn-news-summary">{summaries[i]}</p>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="dd-muted">No stories yet today.</p>
          )}
        </div>
      </section>

      <Disclaimer />

      <ChartModal
        isOpen={Boolean(chart)}
        onClose={() => setChart(null)}
        symbol={chart?.symbol}
        title={chart?.title}
        position={chart?.position || null}
        onChartRequest={chartRequestHandler}
      />
    </div>
  );
}
